/**
 * The shared drive (Maggie's section 7): the folder tree the system makes,
 * named as Admin sets, and the naming rule for what it files:
 * YYYY-MM-DD_Otii_Type_Period_vN, the date the file was made final.
 */
import type { ISODate } from "./dates";
import type { SourceSync } from "./types";

export const TOP_FOLDER_KEYS = ["readMe", "cycles", "board", "finance", "registers", "investors", "company", "framework", "archive"] as const;
export type TopFolderKey = (typeof TOP_FOLDER_KEYS)[number];
export const CYCLE_FOLDER_KEYS = ["sections", "accounts", "meeting", "issued"] as const;
export type CycleFolderKey = (typeof CYCLE_FOLDER_KEYS)[number];

export interface DriveSettings {
  /** The company's shared drive. */
  driveId: string | null;
  driveName: string | null;
  /** In every file name: 2026-10-22_Otii_MonthlyPack_2026-09_v1.pdf. */
  prefix: string;
  folders: Record<TopFolderKey, string>;
  cycle: Record<CycleFolderKey, string>;
  /** A board folder's meeting folder. */
  boardMeeting: string;
}

export const DEFAULT_DRIVE_SETTINGS: DriveSettings = {
  driveId: null,
  driveName: null,
  prefix: "Otii",
  folders: { readMe: "00 Read me", cycles: "01 Monthly cycles", board: "02 Board", finance: "03 Finance", registers: "04 Registers", investors: "05 Investors", company: "06 Company and statutory", framework: "07 Framework and templates", archive: "99 Archive" },
  cycle: { sections: "1 Sections", accounts: "2 Management accounts", meeting: "3 Meeting", issued: "4 Issued" },
  boardMeeting: "Meeting",
};
export const TOP_FOLDER_LABEL: Record<TopFolderKey, string> = { readMe: "Read me", cycles: "Monthly cycles", board: "Board", finance: "Finance", registers: "Registers", investors: "Investors", company: "Company and statutory", framework: "Framework and templates", archive: "Archive" };
export const CYCLE_FOLDER_LABEL: Record<CycleFolderKey, string> = { sections: "Sections", accounts: "Management accounts", meeting: "Meeting", issued: "Issued" };

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const SHORT = MONTHS.map((m) => m.slice(0, 3));

/** "FY2026-27": the financial year a day falls in. */
export function fyLabel(day: ISODate, yearStartMonth: number): string {
  const y = Number(day.slice(0, 4));
  const start = Number(day.slice(5, 7)) >= yearStartMonth ? y : y - 1;
  return yearStartMonth === 1 ? `FY${start}` : `FY${start}-${String((start + 1) % 100).padStart(2, "0")}`;
}
/** "2026-09 September (meets 21 Oct)". */
export const cycleFolderName = (month: string, meetingOn: ISODate) => `${month} ${MONTHS[Number(month.slice(5, 7)) - 1]} (meets ${Number(meetingOn.slice(8, 10))} ${SHORT[Number(meetingOn.slice(5, 7)) - 1]})`;
/** "2026-11-04 Board — July to September 2026". */
export const boardFolderName = (meetingOn: ISODate, period: string) => `${meetingOn} Board — ${period}`;
/** "2026-Q3": the calendar quarter a month ends. */
export const quarterLabel = (month: string) => `${month.slice(0, 4)}-Q${Math.ceil(Number(month.slice(5, 7)) / 3)}`;
/** "2026-10-22_Otii_MonthlyPack_2026-09_v1.pdf". */
export const driveFileName = (day: ISODate, prefix: string, type: string, period: string, version: string) => `${day}_${prefix.replace(/[^A-Za-z0-9-]+/g, "") || "Otii"}_${type}_${period}_${version}.pdf`;

export type DriveFileKind = "monthly_pack" | "board_pack" | "minutes" | "notes";
export const DRIVE_FILE_LABEL: Record<DriveFileKind, string> = { monthly_pack: "Issued pack", board_pack: "Board pack", minutes: "Signed minutes", notes: "Meeting notes" };

export interface DriveFolderRow {
  key: string;
  fileId: string;
  name: string;
  parentKey: string | null;
  webViewLink: string | null;
}

export interface DriveFileRow {
  id: string;
  kind: DriveFileKind;
  entityId: string;
  version: number;
  name: string;
  folderKey: string;
  webViewLink: string | null;
  readOnly: boolean;
  filedAt: string;
  archivedAt: string | null;
}

/** For Records → Drive and Admin. */
export interface DriveState {
  mode: "live" | "demo" | "off";
  /** The Google account (Calendar's) is connected… */
  connected: boolean;
  /** …and has the Drive scope. */
  scoped: boolean;
  email: string | null;
  settings: DriveSettings;
  sync: SourceSync | null;
  folders: DriveFolderRow[];
  files: DriveFileRow[];
}

export const folderUrl = (fileId: string) => `https://drive.google.com/drive/folders/${fileId}`;
